import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { User } from '../modules/platform/types';
import { getCurrentUser } from '../modules/platform/api/users';
import { clearAuthSessionStorage } from '../utils/authSession';
import { getAccessToken, getRefreshToken, setTokens } from '../utils/token';
import * as authApi from '../modules/platform/api/auth';
import type { LoginResponse } from '../modules/platform/types';
import { ROSTER_STUDENT_ME_QUERY_KEY } from '../modules/roster/queryKeys';
import {
  clearLmsLaunchSession,
  isLmsLaunchSession as readLmsLaunchSessionFlag,
  setLmsLaunchSession,
} from '../utils/lmsSession';

function resolveIsLmsLaunchSession(user: User | null): boolean {
  if (user?.auth_source === 'lms') {
    return true;
  }
  if (user?.auth_source != null && user.auth_source !== 'lms') {
    return false;
  }
  return readLmsLaunchSessionFlag();
}

function syncLmsSessionFromUser(user: User): void {
  if (user.auth_source === 'lms') {
    setLmsLaunchSession({
      provider: user.lms_provider,
      launchProfile: user.launch_profile,
    });
    return;
  }
  if (user.auth_source != null && user.auth_source !== 'lms') {
    clearLmsLaunchSession();
  }
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isLmsLaunchSession: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Exchange LMS assertion tokens and load full session (same path as login). */
  completeLmsSession: (response: LoginResponse) => Promise<User>;
  register: (email: string, password: string, role: 'PARENT' | 'STUDENT') => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Fallback to a safe default to avoid crashing during HMR or unexpected render order.
    // This prevents "useAuth must be used within AuthProvider" runtime exceptions.
    // Consumers should still behave as unauthenticated when provider is missing.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const noop = async () => {};
    return {
      user: null,
      loading: false,
      isAuthenticated: false,
      isLmsLaunchSession: false,
      login: async (_email: string, _password: string) => {
        return Promise.reject(new Error('AuthProvider missing'));
      },
      completeLmsSession: async (_response: LoginResponse) => {
        return Promise.reject(new Error('AuthProvider missing'));
      },
      register: async (_email: string, _password: string, _role: 'PARENT' | 'STUDENT') => {
        return Promise.reject(new Error('AuthProvider missing'));
      },
      logout: async () => {
        return Promise.resolve();
      },
      refreshUser: async () => {
        return Promise.resolve();
      },
      hasRole: (_role: string) => false,
      hasAnyRole: (_roles: string[]) => false,
    } as AuthContextType;
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLmsLaunchSession, setIsLmsLaunchSession] = useState(() => readLmsLaunchSessionFlag());
  const loadUserRequestId = useRef(0);

  const applyUserSession = (nextUser: User) => {
    setUser(nextUser);
    syncLmsSessionFromUser(nextUser);
    setIsLmsLaunchSession(resolveIsLmsLaunchSession(nextUser));
  };

  const loadUser = async () => {
    const requestId = ++loadUserRequestId.current;
    const token = getAccessToken();
    if (!token) {
      if (requestId !== loadUserRequestId.current) return;
      setIsLmsLaunchSession(false);
      setLoading(false);
      return;
    }

    try {
      const data = await getCurrentUser();
      if (requestId !== loadUserRequestId.current) return;
      applyUserSession(data.user);
      // Persist identity headers for API interceptors (gateway expects these)
      try {
        const primaryRole = data.user.roles?.[0] ?? data.user.memberships?.[0]?.role;
        const tenantId = data.user.memberships?.[0]?.school_id || '';
        if (primaryRole) localStorage.setItem('X_ROLE', primaryRole);
        if (tenantId) localStorage.setItem('X_TENANT_ID', tenantId);
        if (data.user.id) localStorage.setItem('X_USER_ID', data.user.id);
      } catch {
        // ignore localStorage errors
      }
    } catch (error) {
      if (requestId !== loadUserRequestId.current) return;
      console.error('Failed to load user:', error);
      clearAuthSessionStorage();
      setUser(null);
      setIsLmsLaunchSession(false);
    } finally {
      if (requestId === loadUserRequestId.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const login = async (email: string, password: string) => {
    clearLmsLaunchSession();
    setIsLmsLaunchSession(false);
    const response = await authApi.login({ email, password });
    setTokens(response.access_token, response.refresh_token);
    await loadUser();
  };

  const completeLmsSession = async (response: LoginResponse): Promise<User> => {
    loadUserRequestId.current += 1;
    setTokens(response.access_token, response.refresh_token);
    const token = getAccessToken();
    if (!token) {
      throw new Error('Failed to store session tokens');
    }
    setLmsLaunchSession();
    setIsLmsLaunchSession(true);
    const data = await getCurrentUser();
    applyUserSession(data.user);
    try {
      const primaryRole = data.user.roles?.[0] ?? data.user.memberships?.[0]?.role;
      const tenantId = data.user.memberships?.[0]?.school_id || '';
      if (primaryRole) localStorage.setItem('X_ROLE', primaryRole);
      if (tenantId) localStorage.setItem('X_TENANT_ID', tenantId);
      if (data.user.id) localStorage.setItem('X_USER_ID', data.user.id);
    } catch {
      // ignore localStorage errors
    }
    setLoading(false);
    return data.user;
  };

  const register = async (email: string, password: string, role: 'PARENT' | 'STUDENT') => {
    clearLmsLaunchSession();
    setIsLmsLaunchSession(false);
    const response = await authApi.register({ email, password, role });
    setTokens(response.access_token, response.refresh_token);
    await loadUser();
  };

  const logout = async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    clearAuthSessionStorage();
    setUser(null);
    setIsLmsLaunchSession(false);
    queryClient.removeQueries({ queryKey: ROSTER_STUDENT_ME_QUERY_KEY });
  };

  const refreshUser = async () => {
    await loadUser();
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return user.roles.includes(role as any);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!user) return false;
    return roles.some((role) => user.roles.includes(role as any));
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    isLmsLaunchSession,
    login,
    completeLmsSession,
    register,
    logout,
    refreshUser,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
